import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Link2, Plus, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { fmt, formatApiError, GAME_STYLES, langStyle, preorderGate, preorderGateLabel } from "@/lib/api";
import { useCreatorCode } from "@/lib/creatorLink";
import { useCart } from "@/context/CartContext";
import { useFavorites } from "@/context/FavoritesContext";
import { useAuth } from "@/context/AuthContext";

export default function ProductCard({ product: p, index = 0 }) {
  const { add } = useCart();
  const { user } = useAuth();
  const { favs, toggle } = useFavorites() || { favs: [], toggle: null };
  const creatorCode = useCreatorCode();
  const out = p.in_stock <= 0;
  const isFav = favs.includes(p.id);

  const copiaLinkSponsor = async (e) => {
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/api/og/prodotto/${p.id}?ref=${encodeURIComponent(creatorCode)}`);
      toast.success("Link sponsorizzato copiato — con anteprima social");
    } catch {
      toast.error("Impossibile copiare il link");
    }
  };

  const handleFav = async (e) => {
    e.preventDefault();
    try {
      const favorited = await toggle(p.id);
      toast.success(favorited ? `${p.name} nei preferiti — ti avvisiamo via email al rientro` : `${p.name} rimosso dai preferiti`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const badges = [
    p.game && { key: "game", cls: GAME_STYLES[p.game] || "bg-zinc-700/70 text-white", label: p.game, title: "" },
    p.is_new && { key: "new", cls: "bg-emerald-500/70 text-white", label: "Novità", title: "" },
    p.restocked_recently && { key: "restock", cls: "bg-cyan-500/70 text-white", label: "Tornato disponibile", title: "" },
    p.badge && { key: "badge", cls: "bg-red-600/70 text-white", label: p.badge, title: "" },
  ].filter(Boolean);

  return (
    <motion.article
      data-testid={`product-card-${p.id}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ delay: (index % 3) * 0.08, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className="group relative rounded-xl slab-frame overflow-hidden"
    >
      <Link to={`/prodotto/${p.id}`} data-testid={`product-link-${p.id}`} className="block relative aspect-square sm:aspect-[4/5] overflow-hidden bg-black">
        {p.image ? (
          <>
            <img src={p.image} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover blur-2xl opacity-30 scale-110" />
            <img
              src={p.image}
              alt={p.name}
              loading="lazy"
              className="relative w-full h-full object-contain p-2 opacity-95 group-hover:scale-[1.03] group-hover:opacity-100 transition-all duration-700"
            />
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-[#0D0D0E]">
            <span className="text-zinc-700 font-mono text-xs uppercase tracking-[0.3em]">Wanted</span>
          </div>
        )}
        {p.bundle_name && (p.bundle_discount || 0) > 0 && (
          <span data-testid={`bundle-badge-${p.id}`} title={`Fa parte del bundle "${p.bundle_name}": prendili tutti insieme e risparmi il ${p.bundle_discount}%`} className="absolute bottom-3 left-3 z-10 text-[10px] font-mono uppercase tracking-widest font-bold bg-amber-500 text-white rounded px-2.5 py-1.5">
            Bundle -{p.bundle_discount}%
          </span>
        )}
        {p.is_preorder && (
          <span data-testid={`preorder-badge-${p.id}`} className="absolute bottom-3 left-3 text-[10px] font-mono uppercase tracking-widest font-bold bg-amber-500 text-white rounded px-2.5 py-1.5">
            Preordine{p.release_date ? ` • esce ${new Date(p.release_date).toLocaleDateString("it-IT", { day: "2-digit", month: "short" })}` : ""}
          </span>
        )}
        {out && (
          <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
            <span className="font-display font-bold uppercase tracking-widest text-zinc-400 border border-zinc-600 px-4 py-2 rounded">Esaurito</span>
          </div>
        )}
        {creatorCode && (
          <button
            data-testid={`creator-share-${p.id}`}
            onClick={copiaLinkSponsor}
            title="Copia il link sponsorizzato col tuo codice creator"
            className="absolute bottom-3 right-14 z-10 p-2.5 rounded-full bg-black/70 backdrop-blur-sm border border-red-600/50 hover:border-red-500 transition-all active:scale-[0.92]"
          >
            <Link2 size={14} className="text-red-400" />
          </button>
        )}
        {user && (
          <button
            data-testid={`favorite-${p.id}`}
            onClick={handleFav}
            title={isFav ? "Rimuovi dai preferiti" : "Aggiungi ai preferiti: ti avvisiamo via email al rientro"}
            aria-pressed={isFav}
            className={`absolute bottom-3 right-3 z-10 p-2.5 rounded-full bg-black/70 backdrop-blur-sm border transition-all active:scale-[0.92] ${isFav ? "border-red-600/60" : "border-white/20 hover:border-red-600/60"}`}
          >
            <Heart size={14} className={isFav ? "fill-red-500 text-red-500" : "text-zinc-300"} />
          </button>
        )}
      </Link>

      <div className="p-3.5 sm:p-5">
        <div className="flex flex-wrap items-center gap-1 sm:gap-1.5">
          {badges.map((b) => (
            <span
              key={b.key}
              data-testid={b.key === "new" ? `new-badge-${p.id}` : b.key === "restock" ? `restock-badge-${p.id}` : undefined}
              title={b.title}
              className={`text-[9px] sm:text-[10px] font-mono uppercase tracking-wider font-semibold rounded px-1.5 py-0.5 sm:px-2 sm:py-1 leading-tight ${b.cls}`}
            >
              {b.label}
            </span>
          ))}
        </div>
        <p className="hidden lg:block text-xs font-mono text-zinc-500 uppercase tracking-wider truncate mt-1.5">{p.set}</p>
        <Link to={`/prodotto/${p.id}`} className="flex items-center gap-2 mt-1">
          <h3 className="font-display text-sm sm:text-base lg:text-lg font-bold tracking-tight text-white leading-snug line-clamp-2 group-hover:text-red-400 transition-colors">
            {p.name}
          </h3>
          {p.language && (
            <span data-testid={`lang-badge-${p.id}`} title={`Lingua: ${p.language}`} className={`text-[9px] sm:text-[10px] font-mono uppercase tracking-widest font-bold rounded px-1.5 py-0.5 sm:px-2 sm:py-1 leading-tight shrink-0 ${langStyle(p.language)}/70`}>
              {p.language}
            </span>
          )}
        </Link>
        {/* Mobile/tablet: card essenziale (foto, nome, prezzo, aggiungi) — dettagli extra solo da desktop */}
        <p className="hidden lg:block text-[11px] font-mono uppercase tracking-wider text-zinc-500 mt-2">
          {p.grading && <span className="text-red-400 border border-red-600/40 bg-red-600/10 rounded px-1.5 py-0.5 mr-2 font-bold">{p.grading}{p.grade ? ` ${p.grade}` : ""}</span>}
          {p.condition}
          {!out && (
            <span data-testid={`product-stock-${p.id}`} className={`block mt-1.5 ${p.in_stock <= 3 ? "text-amber-400" : "text-zinc-500"}`}>
              {p.in_stock <= 3 ? `Ultimi ${p.in_stock} pz` : `${p.in_stock} disponibili`}
            </span>
          )}
        </p>
        {p.tags?.length > 0 && (
          <div className="hidden lg:flex flex-wrap gap-1.5 mt-2" data-testid={`product-tags-${p.id}`}>
            {p.tags.slice(0, 3).map((t) => (
              <span key={t} className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 border border-white/15 rounded px-1.5 py-0.5">{t}</span>
            ))}
          </div>
        )}

        <div className="flex flex-col lg:flex-row lg:flex-wrap lg:items-end lg:justify-between gap-2.5 mt-3">
          <div>
            {(p.sale_percent || 0) > 0 && p.final_price ? (
              <div>
                <p className="font-mono text-xs text-zinc-500 line-through">{fmt(p.price)}</p>
                <p className="font-mono font-bold text-base sm:text-lg text-emerald-400">
                  {fmt(p.final_price)} <span data-testid={`sale-badge-${p.id}`} className="text-[10px] font-bold text-emerald-500 border border-emerald-500/40 rounded px-1.5 py-0.5 ml-1 align-middle">-{p.sale_percent}%</span>
                </p>
              </div>
            ) : (
              <p className="font-mono font-bold text-base sm:text-lg text-white">{fmt(p.price)}</p>
            )}
            <p className="hidden lg:block text-xs font-mono text-red-500 font-semibold">+{p.points} punti</p>
            {p.mkm?.trend > 0 && (() => {
              const eff = p.final_price || p.price;
              const under = eff < p.mkm.trend;
              return (
                <p
                  data-testid={`mkm-trend-${p.id}`}
                  title="Prezzo medio di vendita su Cardmarket, aggiornato ogni notte"
                  className={`inline-flex items-center gap-1 max-w-full text-[9px] sm:text-[10px] font-mono rounded-full px-2 py-0.5 sm:px-2.5 sm:py-1 mt-2 border ${under ? "border-emerald-500/40 text-emerald-300" : "border-orange-400/40 text-orange-300"}`}
                >
                  <TrendingUp size={10} className="shrink-0" />
                  <span className="truncate">Trend {fmt(p.mkm.trend)}{under ? <span className="hidden sm:inline"> · sotto mercato</span> : null}</span>
                </p>
              );
            })()}
          </div>
          {preorderGate(p) ? (
            <span data-testid={`preorder-closed-${p.id}`} title={preorderGateLabel(p)} className="inline-flex items-center justify-center gap-1.5 w-full lg:w-auto border border-amber-500/40 text-amber-500/80 text-xs font-display font-bold uppercase tracking-wide px-4 py-2.5 rounded-full cursor-not-allowed select-none shrink-0">
              Presto
            </span>
          ) : (
          <button
            data-testid={`add-to-cart-${p.id}`}
            disabled={out}
            onClick={() => { add(p); toast.success(`${p.name} aggiunto al carrello`); }}
            className="inline-flex items-center justify-center gap-1.5 w-full lg:w-auto bg-red-600 hover:bg-red-700 border border-red-600 disabled:opacity-30 disabled:cursor-not-allowed text-white text-[11px] lg:text-xs font-display font-bold uppercase tracking-wide px-3.5 py-2 lg:px-4 lg:py-2.5 rounded-full transition-all active:scale-[0.96] shrink-0"
          >
            <Plus size={14} /> Aggiungi
          </button>
          )}
        </div>
      </div>
    </motion.article>
  );
}
